import * as React from "react";
import { ChevronDown } from "lucide-react";
import { observeResize } from "@/lib/scrollMore";
import { cn } from "@/lib/utils";

/**
 * "There is more below" — for a panel whose list can be taller than the panel.
 *
 * The vertical twin of the sideways hint (lib/scrollMore.ts, `.scroll-hint`).
 * iOS's overlay scrollbar is invisible at rest, so a menu sheet whose last
 * visible row happens to end cleanly at the bottom edge looked FINISHED: the
 * Switch Context group beneath it was simply never found (Andrew, 2026-09-28:
 * "in the mobile menu drawer, indicate with an arrow that you can scroll down
 * for more").
 *
 * Wrap the scroll region — its ONE child, e.g. a `DialogBody` — and this:
 *
 *  - marks the scroller `data-more-below="true"` while it can scroll further
 *    down, which `.scroll-fade-y` in index.css answers with a mask fading the
 *    bottom edge into whatever surface is behind it (a mask, not a gradient
 *    overlay, for the reason the sideways hint gives: the menu panel is itself
 *    a gradient, and a mask does not need to know the colour it fades into);
 *  - pins a chevron at the bottom centre, over that fade, shown only while
 *    there is more. A tap scrolls about one screen down — smoothly, unless the
 *    reader asked for reduced motion.
 *
 * The chevron is aria-hidden and out of the tab order ON PURPOSE. It adds
 * nothing a keyboard or a screen reader lacks — the scroller is a focusable
 * region and every row in it is reachable by Tab, which scrolls it into view —
 * and a focusable control that disappears the moment it has done its job
 * would drop focus to <body>, out of the dialog's focus trap.
 *
 * The frame is `relative flex-1 min-h-0 flex-col` so that, as a direct child
 * of DialogContent's flex column, it passes the column's bounded height down
 * to the scroller exactly as the scroller had it before; and because it ends
 * where the scroller ends, the chevron sits above the sheet's own
 * `--sa-bottom` padding with no inset arithmetic of its own.
 */
export function ScrollDownHint({ children, className }: { children: React.ReactElement; className?: string }) {
  const frame = React.useRef<HTMLDivElement>(null);
  const [more, setMore] = React.useState(false);

  React.useEffect(() => {
    const scroller = frame.current?.firstElementChild;
    if (!(scroller instanceof HTMLElement)) return;
    scroller.classList.add("scroll-fade-y");

    // Scroll events read three numbers and write at most one attribute and
    // one state change; no layout is forced beyond what scrolling already did.
    const update = () => {
      const below = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop > 2;
      const flag = below ? "true" : "false";
      if (scroller.dataset.moreBelow !== flag) scroller.dataset.moreBelow = flag;
      setMore(below);
    };

    // The box resizes (the sheet expands, the keyboard, a rotation) and so
    // does its content (the project's details load after the sheet opens), so
    // both sides of the comparison are watched — through the app's one shared
    // ResizeObserver. Children are re-bound if the scroller's children change.
    let offChildren: Array<() => void> = [];
    const bindChildren = () => {
      offChildren.forEach((off) => off());
      offChildren = Array.from(scroller.children).map((c) => observeResize(c, update));
      update();
    };
    const offSelf = observeResize(scroller, update);
    bindChildren();
    const mo = new MutationObserver(bindChildren);
    mo.observe(scroller, { childList: true });
    scroller.addEventListener("scroll", update, { passive: true });

    return () => {
      scroller.removeEventListener("scroll", update);
      mo.disconnect();
      offSelf();
      offChildren.forEach((off) => off());
      scroller.classList.remove("scroll-fade-y");
      delete scroller.dataset.moreBelow;
    };
  }, []);

  const scrollDown = () => {
    const scroller = frame.current?.firstElementChild;
    if (!(scroller instanceof HTMLElement)) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // Most of a screen, not all of it: the last row or two of what was on
    // screen stay visible at the top, so the reader keeps their place.
    scroller.scrollBy({ top: Math.round(scroller.clientHeight * 0.8), behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <div ref={frame} className={cn("relative flex min-h-0 flex-1 flex-col", className)}>
      {children}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="scroll-down-hint"
        data-show={more ? "true" : "false"}
        onClick={scrollDown}
        className={cn(
          "absolute bottom-1 left-1/2 z-raised flex min-h-touch min-w-touch -translate-x-1/2 items-center justify-center outline-hidden",
          "transition-[opacity,transform] duration-fast ease-standard motion-reduce:transition-none",
          more ? "opacity-100" : "pointer-events-none translate-y-1 opacity-0"
        )}
      >
        {/* The Switch Project button's film, so on the menu panel it is the
            same white glass (index.css `.menu-panel .glass-button`). */}
        <span className="glass-button flex size-8 items-center justify-center rounded-full">
          <ChevronDown className="size-4" strokeWidth={2.25} />
        </span>
      </button>
    </div>
  );
}
